import Vue from "vue";
import { createAuth0Client } from "@auth0/auth0-spa-js";
import { AUTH0_CALLBACK_ROUTE } from "./poSession";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const safeLogoutReturnTo = value => {
  try {
    const target = new URL(value || window.location.origin, window.location.origin);
    if (target.origin === window.location.origin) return target.href;
  } catch (error) {
    // Fall through to the fixed same-origin default.
  }
  return window.location.origin;
};

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    methods: {
      async loginWithPopup(options, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(options, config);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.popupOpen = false;
        }
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          const result = await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
          return result;
        } catch (e) {
          this.error = e;
          throw e;
        } finally {
          this.loading = false;
        }
      },
      loginWithRedirect(o) {
        if (o) {
          const { appState, authorizationParams = {}, ...rest } = o;
          return this.auth0Client.loginWithRedirect({
            ...rest,
            appState,
            authorizationParams: {
              ...authorizationParams,
              redirect_uri: redirectUri
            }
          });
        }
        return this.auth0Client.loginWithRedirect({
          authorizationParams: {
            redirect_uri: redirectUri
          }
        });
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        if (o) {
          const { audience, authorizationParams = {}, ...rest } = o;
          return this.auth0Client.getTokenSilently({
            ...rest,
            authorizationParams: {
              ...authorizationParams,
              ...(audience ? { audience } : {})
            }
          });
        }
        return this.auth0Client.getTokenSilently();
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        if (o) {
          const { returnTo, logoutParams = {}, ...rest } = o;
          return this.auth0Client.logout({
            ...rest,
            logoutParams: {
              ...logoutParams,
              returnTo: safeLogoutReturnTo(returnTo)
            }
          });
        }
        return this.auth0Client.logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      }
    },
    async created() {
      const {
        domain,
        clientId,
        authorizationParams = {},
        ...clientOptions
      } = options;
      try {
        if (!domain || !clientId) {
          throw new Error("Auth0 frontend configuration is unavailable.");
        }
        this.auth0Client = await createAuth0Client({
          domain,
          clientId,
          ...clientOptions,
          authorizationParams: {
            redirect_uri: redirectUri,
            ...authorizationParams
          }
        });

        const callbackParams = new URLSearchParams(window.location.search);
        const hasCode = callbackParams.has("code");
        const hasState = callbackParams.has("state");
        const hasError = callbackParams.has("error");

        // Only handle callback automatically if NOT on the manual callback path
        if ((hasCode || hasError) && hasState && window.location.pathname !== AUTH0_CALLBACK_ROUTE) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        if (this.auth0Client) {
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.user = await this.auth0Client.getUser();
        } else {
          this.isAuthenticated = false;
          this.user = {};
        }
        this.loading = false;
      }
    }
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
